import React, { useMemo } from "react";
import { Button } from "./button";
import { Card, CardContent } from "./card";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "./table";
import { 
  ChevronLeftIcon,
  ChevronRightIcon
} from "lucide-react";
import { cn } from "../../lib/utils";

export interface PaginatedTableColumn<T> {
  key: keyof T;
  header: React.ReactNode;
  width?: string;
  headerClassName?: string;
  cellClassName?: string;
  sortable?: boolean;
  render?: (item: T, index: number) => React.ReactNode;
}

export interface SortConfig<T> {
  key: keyof T | null;
  direction: "asc" | "desc";
}

export interface PaginatedTableProps<T> {
  data: T[];
  columns: PaginatedTableColumn<T>[];
  loading?: boolean;
  sortConfig?: SortConfig<T>;
  onSort?: (key: keyof T) => void;
  currentPage: number;
  onPageChange: (page: number) => void;
  pageSize: number;
  totalItems?: number; // For server-side pagination
  emptyTitle?: string;
  emptyDescription?: string;
  onRowClick?: (item: T, index: number) => void;
  rowClassName?: string | ((item: T, index: number) => string);
  /** Merged onto each `<tr>` after `rowClassName` (e.g. `title` for tooltips). */
  getRowProps?: (
    item: T,
    index: number
  ) => React.HTMLAttributes<HTMLTableRowElement>;
  showPagination?: boolean;
  loadingRows?: number;
  defaultSort?: (a: T, b: T) => number;
  serverSidePagination?: boolean; // New prop to indicate server-side pagination
}

function PaginatedTable<T extends Record<string, any>>({
  data,
  columns,
  loading = false,
  sortConfig,
  onSort,
  currentPage,
  onPageChange,
  pageSize,
  totalItems,
  emptyTitle = "No data found",
  emptyDescription = "No items to display.",
  onRowClick,
  rowClassName,
  getRowProps,
  showPagination = true,
  loadingRows = 5,
  defaultSort,
  serverSidePagination = false,
}: PaginatedTableProps<T>) {

  // Sort data (only for client-side pagination)
  const sortedData = useMemo(() => {
    if (serverSidePagination) {
      // For server-side pagination, assume data is already sorted and paginated
      return data;
    }
    
    const sortableData = [...data];
    if (sortConfig?.key && onSort) {
      sortableData.sort((a, b) => {
        const aValue = a[sortConfig.key!];
        const bValue = b[sortConfig.key!];
        
        if (aValue < bValue) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    } else if (defaultSort) {
      sortableData.sort(defaultSort);
    }
    return sortableData;
  }, [data, sortConfig, defaultSort, onSort, serverSidePagination]);

  // Paginate data (only for client-side pagination)
  const paginatedData = useMemo(() => {
    if (serverSidePagination) {
      // For server-side pagination, data is already paginated
      return sortedData;
    }
    
    return sortedData.slice(
      (currentPage - 1) * pageSize,
      currentPage * pageSize
    );
  }, [sortedData, currentPage, pageSize, serverSidePagination]);

  // Calculate total pages using totalItems for server-side pagination
  const totalPages = useMemo(() => {
    if (serverSidePagination && totalItems !== undefined) {
      return Math.ceil(totalItems / pageSize);
    }
    return Math.ceil(sortedData.length / pageSize);
  }, [serverSidePagination, totalItems, pageSize, sortedData.length]);

  // Calculate total count for display
  const totalCount = useMemo(() => {
    if (serverSidePagination && totalItems !== undefined) {
      return totalItems;
    }
    return sortedData.length;
  }, [serverSidePagination, totalItems, sortedData.length]);

  // Calculate visible page numbers with ellipsis
  const visiblePages = useMemo(() => {
    const delta = 2; // Number of pages to show on each side of current page
    const range = [];
    const rangeWithDots = [];

    // Always show first page
    range.push(1);

    // Calculate start and end of the visible range around current page
    const start = Math.max(2, currentPage - delta);
    const end = Math.min(totalPages - 1, currentPage + delta);

    // Add pages around current page
    for (let i = start; i <= end; i++) {
      range.push(i);
    }

    // Always show last page if there are multiple pages
    if (totalPages > 1) {
      range.push(totalPages);
    }

    // Remove duplicates and sort
    const uniqueRange = Array.from(new Set(range)).sort((a, b) => a - b);

    // Add ellipsis where needed
    for (let i = 0; i < uniqueRange.length; i++) {
      const current = uniqueRange[i];
      const next = uniqueRange[i + 1];

      rangeWithDots.push(current);

      // Add ellipsis if there's a gap > 1
      if (next && next - current > 1) {
        rangeWithDots.push("...");
      }
    }

    return rangeWithDots;
  }, [currentPage, totalPages]);

  // Loading state - show full skeleton only for initial loading with no data
  // For simple tables without pagination (like IntentsTable), we show full skeleton during any loading
  // For paginated tables, we only show full skeleton when there's no data (initial empty state)
  if (loading && data.length === 0) {
    return (
      <Card className="border-border">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-b border-border hover:bg-transparent">
                {columns.map((column, columnIndex) => (
                  <TableHead
                    key={`${String(column.key)}-${columnIndex}`}
                    className={cn(column.width, column.headerClassName)}
                  >
                    <div className="h-4 bg-muted rounded w-16 animate-pulse" />
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {[...Array(loadingRows)].map((_, i) => (
                <TableRow key={i} className="border-b border-border/50">
                  {columns.map((column, columnIndex) => (
                    <TableCell
                      key={`${String(column.key)}-${columnIndex}`}
                      className={cn("py-4", column.width, column.cellClassName)}
                    >
                      <div className="h-4 bg-muted rounded animate-pulse" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    );
  }

  // For tables without pagination, show full skeleton during any loading state
  if (loading && !showPagination) {
    return (
      <Card className="border-border">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-b border-border hover:bg-transparent">
                {columns.map((column, columnIndex) => (
                  <TableHead
                    key={`${String(column.key)}-${columnIndex}`}
                    className={cn(column.width, column.headerClassName)}
                  >
                    <div className="h-4 bg-muted rounded w-16 animate-pulse" />
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {[...Array(loadingRows)].map((_, i) => (
                <TableRow key={i} className="border-b border-border/50">
                  {columns.map((column, columnIndex) => (
                    <TableCell
                      key={`${String(column.key)}-${columnIndex}`}
                      className={cn("py-4", column.width, column.cellClassName)}
                    >
                      <div className="h-4 bg-muted rounded animate-pulse" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    );
  }

  // Empty state
  if (!loading && data.length === 0) {
    return (
      <Card className="border-border">
        <CardContent className="p-0">
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="space-y-3">
              <h3 className="text-lg font-gc-med text-foreground">
                {emptyTitle}
              </h3>
              <p className="text-muted-foreground max-w-md">
                {emptyDescription}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const handleSort = (key: keyof T) => {
    if (onSort) {
      onSort(key);
    }
  };

  return (
    <Card className="border-border">
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow className="border-b border-border hover:bg-transparent">
              {columns.map((column, columnIndex) => (
                <TableHead 
                  key={`${String(column.key)}-${columnIndex}`}
                  className={cn(
                    column.width,
                    column.headerClassName,
                    column.sortable && onSort && "cursor-pointer hover:bg-accent/20 transition-colors"
                  )}
                  onClick={column.sortable && onSort ? () => handleSort(column.key) : undefined}
                >
                  {column.sortable && onSort ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSort(column.key);
                      }}
                      className={cn(
                        "flex items-center gap-2 font-gc-med text-muted-foreground hover:text-foreground transition-colors",
                        sortConfig?.key === column.key && "text-maitai-green"
                      )}
                    >
                      {column.header}
                      {sortConfig?.key === column.key && (
                        <span className="text-xs">
                          {sortConfig.direction === "asc" ? "↑" : "↓"}
                        </span>
                      )}
                    </button>
                  ) : (
                    column.header
                  )}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              // Show skeleton rows when loading during pagination
              [...Array(loadingRows)].map((_, i) => (
                <TableRow key={`loading-${i}`} className="border-b border-border/50">
                  {columns.map((column, columnIndex) => (
                    <TableCell key={`${String(column.key)}-${columnIndex}`} className="py-4">
                      <div className="h-4 bg-muted rounded animate-pulse" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              paginatedData.map((item, index) => {
                const resolvedRowClassName = typeof rowClassName === "function" 
                  ? rowClassName(item, index) 
                  : rowClassName;
                const extraRowProps = getRowProps?.(item, index) ?? {};
                const { className: extraRowClassName, ...restRowProps } =
                  extraRowProps;

                return (
                  <TableRow
                    key={item.id || index}
                    className={cn(
                      "border-b border-border/50",
                      onRowClick && "cursor-pointer hover:bg-accent/20 transition-colors",
                      resolvedRowClassName,
                      extraRowClassName
                    )}
                    onClick={
                      onRowClick ? () => onRowClick(item, index) : undefined
                    }
                    {...restRowProps}
                  >
                    {columns.map((column, columnIndex) => (
                      <TableCell
                        key={`${String(column.key)}-${columnIndex}`}
                        className={cn("py-4", column.width, column.cellClassName)}
                      >
                        {column.render ? column.render(item, index) : String(item[column.key] || "")}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>

        {/* Pagination */}
        {showPagination && totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-border">
            <div className="text-sm text-muted-foreground">
              Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} items
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onPageChange(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="gap-1"
              >
                <ChevronLeftIcon className="w-4 h-4" />
                Previous
              </Button>
              <div className="flex items-center gap-1">
                {visiblePages.map((pageItem, i) => {
                  if (pageItem === "...") {
                    return (
                      <span key={`ellipsis-${i}`} className="px-2 text-muted-foreground">
                        ...
                      </span>
                    );
                  }
                  
                  const pageNum = pageItem as number;
                  const isCurrentPage = pageNum === currentPage;
                  return (
                    <Button
                      key={pageNum}
                      variant={isCurrentPage ? "default" : "ghost"}
                      size="sm"
                      onClick={() => onPageChange(pageNum)}
                      className={cn(
                        "w-8 h-8 p-0",
                        isCurrentPage && "bg-maitai-green text-white hover:bg-maitai-green/90"
                      )}
                    >
                      {pageNum}
                    </Button>
                  );
                })}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="gap-1"
              >
                Next
                <ChevronRightIcon className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default PaginatedTable; 